import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  AtSign,
  CheckCircle,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  MoreHorizontal,
} from "lucide-react";
import * as React from "react";
import { BaseEntityCardQuickChat } from "./BaseEntityCardQuickChat";

// =============================================================================
// Context for sharing card state
// =============================================================================

type BaseEntityCardContextValue = {
  onClick?: () => void;
};

const BaseEntityCardContext = React.createContext<BaseEntityCardContextValue>(
  {},
);

const useBaseEntityCard = () => React.useContext(BaseEntityCardContext);

// =============================================================================
// Process Types
// =============================================================================

export type ProcessStatus =
  | "pending"
  | "processing"
  | "complete"
  | "error"
  | "needs_attention";

export type Process = {
  /** Unique identifier for the process */
  id: string;
  /** Display label for the process */
  label: string;
  /** Current status */
  status: ProcessStatus;
  /** Custom content shown when expanded (ReactNode) */
  expandedContent?: React.ReactNode;
  /** Optional icon override */
  icon?: React.ReactNode;
  /** Optional click handler for the process indicator */
  onClick?: () => void;
};

// =============================================================================
// Context Menu Types (defined early for use in BaseEntityCardProps)
// =============================================================================

export type ContextMenuAction = {
  /** Unique key for the action */
  key: string;
  /** Label to display in the menu */
  label: string;
  /** Optional icon to display before the label */
  icon?: React.ReactNode;
  /** Handler called when the action is clicked */
  onClick: () => void;
  /** Whether this action is destructive (will be styled in red) */
  destructive?: boolean;
  /** Whether this action is disabled */
  disabled?: boolean;
  /** Toggle actions: renders the toolbar button in its pressed state. */
  active?: boolean;
  /**
   * Toolbar tint. Entity actions default to the accent (plum) treatment used
   * for AI/process actions; pass false for standard chrome — the icon reads
   * in the default foreground (black/white per theme) until active.
   */
  accent?: boolean;
  /**
   * When present the toolbar button becomes a dropdown trigger.
   * The button itself does NOT fire `onClick`; items fire their own handlers.
   */
  dropdownItems?: { key: string; label: string; onClick: () => void }[];
};

export type ContextMenuGroup = {
  /** Actions in this group */
  actions: ContextMenuAction[];
};

// =============================================================================
// BaseEntityCard - Root container
// =============================================================================

export type BaseEntityCardProps = {
  /** Handler for card click */
  onClick?: () => void;
  /** Handler for double-click on the card */
  onDoubleClick?: () => void;
  /** Handler for mention button click */
  onMentionClick?: () => void;
  /** Handler for quick chat submission */
  onQuickChatSubmit?: (message: string) => void;
  /** Handler for delete button click */
  onDeleteClick?: () => void;
  /** Handler for detail/fullscreen button click */
  onDetailClick?: () => void;
  /** Platform identifier for the entity */
  platform?: string;
  /** Context menu actions */
  actions?: ContextMenuAction[] | ContextMenuGroup[];
  /** Vertical alignment of content - use "center" when only title is shown */
  align?: "start" | "center";
  /** Array of processes to display as indicators */
  processes?: Process[];
  /** Toolbar mode - "hover" shows inline actions, "external" lets parent handle toolbar rendering */
  toolbarMode?: "hover" | "external";
  /** Whether chat is currently open - for external control */
  chatOpen?: boolean;
  /** Callback when chat open state changes - for external control */
  onChatOpenChange?: (open: boolean) => void;
  /** Optional className for styling */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
  /**
   * Inner content layout. `"row"` is the default horizontal icon + content
   * layout; `"stack"` renders children in a vertical column with no padding
   * (for media cards whose body fills the card).
   */
  layout?: "row" | "stack";
  /** Children elements */
  children?: React.ReactNode;
};

const BaseEntityCard = React.forwardRef<HTMLDivElement, BaseEntityCardProps>(
  (
    {
      className,
      style,
      onClick,
      onDoubleClick,
      onQuickChatSubmit,
      // onDeleteClick and onDetailClick are only used by parent when toolbarMode="external"
      // onDeleteClick,
      // onDetailClick,
      align = "start",
      actions,
      processes,
      toolbarMode = "hover",
      chatOpen: controlledChatOpen,
      onChatOpenChange,
      layout = "row",
      children,
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [internalChatOpen, setInternalChatOpen] = React.useState(false);

    // Use controlled or uncontrolled chat state
    const isChatOpen =
      controlledChatOpen !== undefined ? controlledChatOpen : internalChatOpen;
    const setChatOpen = (open: boolean) => {
      if (onChatOpenChange) {
        onChatOpenChange(open);
      } else {
        setInternalChatOpen(open);
      }
    };

    // Add quick chat action if onQuickChatSubmit is provided and mode is hover
    const allActions = React.useMemo(() => {
      const baseActions = actions || [];

      // Only add chat action in hover mode (external mode handles it separately)
      if (onQuickChatSubmit && toolbarMode === "hover") {
        const chatAction: ContextMenuAction = {
          key: "quick-chat",
          label: "Ask",
          icon: <MessageSquare className="h-3.5 w-3.5" />,
          onClick: () => setChatOpen(true),
        };

        // Add chat action at the beginning
        if (baseActions.length === 0) {
          return [chatAction];
        }

        // Handle grouped actions
        if ("actions" in baseActions[0]) {
          return [chatAction, ...baseActions];
        }

        // Handle flat actions
        return [chatAction, ...(baseActions as ContextMenuAction[])];
      }

      return baseActions;
    }, [actions, onQuickChatSubmit, toolbarMode]);

    const flatActions = React.useMemo(() => {
      if (!allActions || allActions.length === 0) return [];
      // Flatten grouped actions into a single array
      if ("actions" in allActions[0]) {
        return (allActions as ContextMenuGroup[]).flatMap((g) => g.actions);
      }
      return allActions as ContextMenuAction[];
    }, [allActions]);
    const hasActions = flatActions.length > 0 && toolbarMode === "hover";

    return (
      <BaseEntityCardContext.Provider value={{ onClick }}>
        <div
          ref={ref}
          className="w-full relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Hover Actions Toolbar */}
          <AnimatePresence>
            {isHovered && hasActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 4 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                }}
                className="absolute -top-9 left-1/2 -translate-x-1/2 z-50"
              >
                <div className="flex items-center gap-1 p-1 rounded-lg bg-popover border shadow-lg whitespace-nowrap">
                  {flatActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                      }}
                      disabled={action.disabled}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors",
                        "hover:bg-accent",
                        action.disabled && "opacity-50 cursor-not-allowed",
                        action.destructive &&
                          "text-destructive hover:bg-destructive/10",
                      )}
                    >
                      {action.icon && (
                        <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">
                          {action.icon}
                        </span>
                      )}
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Card
            className={cn("w-full transition-colors", className, {
              "cursor-pointer": typeof onClick === "function",
            })}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            style={style}
          >
            <div
              className={cn(
                layout === "stack"
                  ? // h-full/min-h-0 keep a fixed-height card (canvas nodes set an
                    // explicit height) from being outgrown by its media, so the
                    // footer row stays inside the box instead of being clipped.
                    "flex flex-col h-full min-h-0"
                  : cn("flex gap-3 p-3", {
                      "items-start": align === "start",
                      "items-center": align === "center",
                    }),
              )}
            >
              {children}
            </div>
          </Card>

          {/* Quick Chat Input - rendered for both modes when chat is open */}
          {onQuickChatSubmit && toolbarMode === "hover" && (
            <BaseEntityCardQuickChat
              visible={isChatOpen}
              onSubmit={onQuickChatSubmit}
              onClose={() => setChatOpen(false)}
            />
          )}

          {processes && processes.length > 0 && (
            <BaseEntityCardProcesses processes={processes} />
          )}
        </div>
      </BaseEntityCardContext.Provider>
    );
  },
);
BaseEntityCard.displayName = "BaseEntityCard";

// =============================================================================
// BaseEntityCardIcon - Avatar/Icon container (left side)
// =============================================================================

export type BaseEntityCardIconProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardIcon = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardIconProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "h-10 w-10 shrink-0 flex items-center justify-center bg-muted rounded-full",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardIcon.displayName = "BaseEntityCardIcon";

// =============================================================================
// BaseEntityCardContent - Main content container (right side)
// =============================================================================

export type BaseEntityCardContentProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardContent = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardContentProps
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("flex-1 min-w-0", className)} {...props}>
    {children}
  </div>
));
BaseEntityCardContent.displayName = "BaseEntityCardContent";

// =============================================================================
// BaseEntityCardHeader - Header row with title and actions
// =============================================================================

export type BaseEntityCardHeaderProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardHeader = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardHeaderProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center justify-between gap-2 mb-1", className)}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardHeader.displayName = "BaseEntityCardHeader";

// =============================================================================
// BaseEntityCardTitle - Title text with platform icon
// =============================================================================

export type BaseEntityCardTitleProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Platform identifier for displaying platform logo */
  platform?: string;
  /** Custom platform icon element (overrides platform prop) */
  platformIcon?: React.ReactNode;
};

const BaseEntityCardTitle = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardTitleProps
>(({ className, platform, platformIcon, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold truncate min-w-0", className)}
    {...props}
  >
    {platformIcon ||
      (platform && <BaseEntityCardPlatformIcon platform={platform} />)}
    <span className="align-middle">{children}</span>
  </div>
));
BaseEntityCardTitle.displayName = "BaseEntityCardTitle";

// =============================================================================
// BaseEntityCardPlatformIcon - Platform logo image
// =============================================================================

export type BaseEntityCardPlatformIconProps =
  React.ImgHTMLAttributes<HTMLImageElement> & {
    platform: string;
  };

const BaseEntityCardPlatformIcon = ({
  platform,
  className,
  ...props
}: BaseEntityCardPlatformIconProps) => {
  const [hasError, setHasError] = React.useState(false);

  // Hide the icon if it failed to load
  if (hasError) {
    return null;
  }

  return (
    <img
      src={`${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos/${platform}.svg`}
      alt={platform}
      className={cn("inline h-5 w-5 mr-2 align-middle", className)}
      onError={() => setHasError(true)}
      {...props}
    />
  );
};
BaseEntityCardPlatformIcon.displayName = "BaseEntityCardPlatformIcon";

// =============================================================================
// BaseEntityCardActions - Actions container (right side of header)
// =============================================================================

export type BaseEntityCardActionsProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardActions = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardActionsProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-1 shrink-0", className)}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardActions.displayName = "BaseEntityCardActions";

// =============================================================================
// BaseEntityCardAction - Single action button with tooltip
// =============================================================================

export type BaseEntityCardActionProps = {
  /** Icon to display */
  icon: React.ReactNode;
  /** Tooltip text */
  tooltip: string;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Optional href for link actions */
  href?: string;
  /** Additional class names */
  className?: string;
};

const BaseEntityCardAction = ({
  icon,
  tooltip,
  onClick,
  href,
  className,
}: BaseEntityCardActionProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
  };

  const buttonContent = href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
    </a>
  ) : (
    icon
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-6 w-6", className)}
            onClick={href ? undefined : handleClick}
            asChild={!!href}
          >
            {buttonContent}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
BaseEntityCardAction.displayName = "BaseEntityCardAction";

// =============================================================================
// BaseEntityCardMentionAction - Pre-configured mention button
// =============================================================================

export type BaseEntityCardMentionActionProps = {
  onClick: () => void;
  className?: string;
};

const BaseEntityCardMentionAction = ({
  onClick,
  className,
}: BaseEntityCardMentionActionProps) => (
  <BaseEntityCardAction
    icon={<AtSign className="h-4 w-4" />}
    tooltip="Mention in chat"
    onClick={onClick}
    className={className}
  />
);
BaseEntityCardMentionAction.displayName = "BaseEntityCardMentionAction";

// =============================================================================
// BaseEntityCardExternalLinkAction - Pre-configured external link button
// =============================================================================

export type BaseEntityCardExternalLinkActionProps = {
  href: string;
  tooltip?: string;
  className?: string;
};

const BaseEntityCardExternalLinkAction = ({
  href,
  tooltip = "Open in web",
  className,
}: BaseEntityCardExternalLinkActionProps) => (
  <BaseEntityCardAction
    icon={<ExternalLink className="h-4 w-4" />}
    tooltip={tooltip}
    href={href}
    className={className}
  />
);
BaseEntityCardExternalLinkAction.displayName =
  "BaseEntityCardExternalLinkAction";

// =============================================================================
// BaseEntityCardContextMenu - Three-dot context menu
// =============================================================================

export type BaseEntityCardContextMenuProps = {
  /** Menu actions - can be flat array or grouped */
  actions: ContextMenuAction[] | ContextMenuGroup[];
  /** Additional class names for the trigger button */
  className?: string;
};

function isContextMenuGroup(
  item: ContextMenuAction | ContextMenuGroup,
): item is ContextMenuGroup {
  return "actions" in item;
}

const BaseEntityCardContextMenu = ({
  actions,
  className,
}: BaseEntityCardContextMenuProps) => {
  const renderMenuItem = (action: ContextMenuAction) => (
    <DropdownMenuItem
      key={action.key}
      onClick={(e) => {
        e.stopPropagation();
        action.onClick();
      }}
      disabled={action.disabled}
      className={cn({
        "text-destructive focus:text-destructive": action.destructive,
      })}
    >
      {action.icon && <span className="mr-2">{action.icon}</span>}
      {action.label}
    </DropdownMenuItem>
  );

  const renderContent = () => {
    if (actions.length > 0 && isContextMenuGroup(actions[0])) {
      const groups = actions as ContextMenuGroup[];
      return groups.map((group, groupIndex) => (
        <React.Fragment key={groupIndex}>
          {groupIndex > 0 && <DropdownMenuSeparator />}
          {group.actions.map(renderMenuItem)}
        </React.Fragment>
      ));
    }
    return (actions as ContextMenuAction[]).map(renderMenuItem);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-6 w-6", className)}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{renderContent()}</DropdownMenuContent>
    </DropdownMenu>
  );
};
BaseEntityCardContextMenu.displayName = "BaseEntityCardContextMenu";

// =============================================================================
// BaseEntityCardSubtitle - Secondary text row
// =============================================================================

export type BaseEntityCardSubtitleProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardSubtitle = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardSubtitleProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-foreground line-clamp-1", className)}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardSubtitle.displayName = "BaseEntityCardSubtitle";

// =============================================================================
// BaseEntityCardDescription - Body text row
// =============================================================================

export type BaseEntityCardDescriptionProps =
  React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardDescription = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardDescriptionProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-xs text-muted-foreground line-clamp-2", className)}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardDescription.displayName = "BaseEntityCardDescription";

// =============================================================================
// BaseEntityCardBody - Content body container (for subtitle, description, etc)
// =============================================================================

export type BaseEntityCardBodyProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardBody = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardBodyProps
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...props}>
    {children}
  </div>
));
BaseEntityCardBody.displayName = "BaseEntityCardBody";

// =============================================================================
// BaseEntityCardTags - Tags/badges row
// =============================================================================

export type BaseEntityCardTagsProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardTags = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardTagsProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-wrap gap-2 items-center", className)}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardTags.displayName = "BaseEntityCardTags";

// =============================================================================
// BaseEntityCardMetadata - Metadata row
// =============================================================================

export type BaseEntityCardMetadataProps = React.HTMLAttributes<HTMLDivElement>;

const BaseEntityCardMetadata = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardMetadataProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-wrap gap-x-4 gap-y-2 items-center text-xs text-muted-foreground",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
BaseEntityCardMetadata.displayName = "BaseEntityCardMetadata";

// =============================================================================
// BaseEntityCardMetadataItem - Individual metadata item with icon
// =============================================================================

export type BaseEntityCardMetadataItemProps =
  React.HTMLAttributes<HTMLDivElement> & {
    icon?: React.ReactNode;
  };

const BaseEntityCardMetadataItem = React.forwardRef<
  HTMLDivElement,
  BaseEntityCardMetadataItemProps
>(({ className, icon, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-1", className)}
    {...props}
  >
    {icon}
    <span>{children}</span>
  </div>
));
BaseEntityCardMetadataItem.displayName = "BaseEntityCardMetadataItem";

// =============================================================================
// BaseEntityCardProcess - Individual process indicator
// =============================================================================

const getProcessStatusIcon = (status: ProcessStatus) => {
  switch (status) {
    case "pending":
    case "processing":
      return null;
    case "complete":
      return <CheckCircle className="h-4 w-4" />;
    case "error":
      return <AlertCircle className="h-4 w-4" />;
    case "needs_attention":
      return <AlertCircle className="h-4 w-4" />;
  }
};

const getProcessStatusStyles = (status: ProcessStatus) => {
  switch (status) {
    case "pending":
      return "bg-muted text-muted-foreground pl-1 shimmer";
    case "processing":
      return "bg-muted text-muted-foreground pl-1 shimmer";
    case "complete":
      return "bg-spectrum-green-light/30 text-lc-active dark:bg-spectrum-green-dark/30 dark:text-lc-active pl-1";
    case "error":
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-lc-blocked pl-1";
    case "needs_attention":
      return "bg-spectrum-orange-light/30 text-lc-warning dark:bg-spectrum-orange-dark/30 dark:text-lc-warning pl-1";
  }
};

export type BaseEntityCardProcessProps = Process & {
  className?: string;
};

const BaseEntityCardProcess = ({
  id,
  label,
  status,
  icon,
  expandedContent,
  onClick,
  className,
}: BaseEntityCardProcessProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const statusIcon = icon || getProcessStatusIcon(status);
  const statusStyles = getProcessStatusStyles(status);
  const hasExpandedContent = !!expandedContent;
  const isClickable = !!onClick || hasExpandedContent;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // If onClick is provided, call it directly
    if (onClick) {
      onClick();
      return;
    }
    // Otherwise toggle expanded content if available
    if (hasExpandedContent) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <motion.div
      key={id}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0, height: 0, margin: 0 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      className={cn("relative m-0.5", className)}
    >
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors",
          statusStyles,
          isClickable && "cursor-pointer hover:opacity-80",
          !isClickable && "cursor-default",
        )}
      >
        {statusIcon && <span className="self-start">{statusIcon}</span>}
        <span className="text-left">{label}</span>
        {hasExpandedContent && !onClick && (
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-4 w-4" />
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {isExpanded && expandedContent && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 z-50 mt-1"
          >
            <div
              className={cn(
                "p-2 rounded-lg text-xs w-[240px] max-h-[200px] overflow-auto",
                "bg-popover border shadow-lg",
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {expandedContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
BaseEntityCardProcess.displayName = "BaseEntityCardProcess";

// =============================================================================
// BaseEntityCardProcesses - Container for process indicators
// =============================================================================

export type BaseEntityCardProcessesProps = {
  processes: Process[];
  className?: string;
};

const BaseEntityCardProcesses = ({
  processes,
  className,
}: BaseEntityCardProcessesProps) => {
  if (!processes || processes.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap justify-end mt-1.5 px-1", className)}>
      <AnimatePresence>
        {processes.map((process) => (
          <BaseEntityCardProcess key={process.id} {...process} />
        ))}
      </AnimatePresence>
    </div>
  );
};
BaseEntityCardProcesses.displayName = "BaseEntityCardProcesses";

// =============================================================================
// BaseEntityCardSkeleton - Skeleton loader
// =============================================================================

export type BaseEntityCardSkeletonProps = {
  className?: string;
  style?: React.CSSProperties;
  showIcon?: boolean;
  showSubtitle?: boolean;
  showDescription?: boolean;
  showTags?: boolean;
};

const BaseEntityCardSkeleton = ({
  className,
  style,
  showIcon = true,
  showSubtitle = true,
  showDescription = true,
  showTags = false,
}: BaseEntityCardSkeletonProps) => (
  <Card className={cn("w-full transition-colors", className)} style={style}>
    <div className="flex items-start gap-3 p-3">
      {showIcon && <Skeleton className="h-10 w-10 rounded-full shrink-0" />}
      <div className="flex-1 min-w-0">
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-2" />
        {showSubtitle && <Skeleton className="h-4 w-3/4 mb-1.5" />}
        {showDescription && <Skeleton className="h-3 w-1/2 mb-1.5" />}
        {showTags && (
          <div className="flex gap-2 mt-1">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        )}
      </div>
    </div>
  </Card>
);
BaseEntityCardSkeleton.displayName = "BaseEntityCardSkeleton";

// =============================================================================
// Exports
// =============================================================================

export {
  BaseEntityCard,
  BaseEntityCardAction,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardPlatformIcon,
  BaseEntityCardProcess,
  BaseEntityCardProcesses,
  BaseEntityCardSkeleton,
  BaseEntityCardSubtitle,
  BaseEntityCardTags,
  BaseEntityCardTitle,
  useBaseEntityCard,
};
